/**
 * Debug Logs Admin Page
 *
 * Lists all debug reports submitted by users (admin only).
 * Allows viewing details and deleting reports.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import { format } from "date-fns";
import { debugService, type DebugReportSummary } from "../services/debug";
import { authService } from "../services/auth";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/card/card.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "@awesome.me/webawesome/dist/components/switch/switch.js";
import "../components/app-shell";

@customElement("debug-logs-page")
export class DebugLogsPage extends LitElement {
  @state() private reports: DebugReportSummary[] = [];
  @state() private isLoading = true;
  @state() private error = "";
  @state() private includeResolved = false;

  // Delete confirmation state
  @state() private deleteConfirmId: string | null = null;
  @state() private isDeleting = false;

  static styles = css`
    :host {
      display: block;
    }

    .page-content {
      padding: 1rem;
      max-width: 1200px;
      margin: 0 auto;
    }

    h1 {
      margin: 0 0 0.5rem 0;
      font-size: 1.5rem;
      color: #333;
    }

    .subtitle {
      color: #6c757d;
      font-size: 0.875rem;
      margin-bottom: 1.5rem;
    }

    .toolbar {
      display: flex;
      gap: 1rem;
      margin-bottom: 1.5rem;
      align-items: center;
      flex-wrap: wrap;
    }

    .toolbar-spacer {
      flex: 1;
    }

    .reports-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border-radius: 0.5rem;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .reports-table th,
    .reports-table td {
      padding: 0.75rem 1rem;
      text-align: left;
      border-bottom: 1px solid #e9ecef;
    }

    .reports-table th {
      background: #f8f9fa;
      font-weight: 600;
      font-size: 0.75rem;
      text-transform: uppercase;
      color: #6c757d;
    }

    .reports-table tr:hover {
      background: #f8f9fa;
      cursor: pointer;
    }

    .reports-table tr.resolved {
      opacity: 0.6;
    }

    .page-name {
      font-weight: 500;
    }

    .user-note {
      font-size: 0.875rem;
      color: #6c757d;
      max-width: 300px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .user-info {
      font-size: 0.875rem;
    }

    .user-email {
      color: #6c757d;
      font-size: 0.75rem;
    }

    .actions {
      display: flex;
      gap: 0.25rem;
    }

    .empty-state {
      text-align: center;
      padding: 3rem 1rem;
      color: #6c757d;
    }

    .empty-state wa-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      opacity: 0.5;
    }

    /* Delete confirmation dialog */
    wa-dialog.delete-dialog::part(panel) {
      max-width: 400px;
    }

    .delete-dialog-content {
      text-align: center;
      padding: 1rem;
    }

    .delete-dialog-content wa-icon {
      font-size: 3rem;
      color: #dc3545;
      margin-bottom: 1rem;
    }

    .delete-dialog-actions {
      display: flex;
      gap: 0.5rem;
      justify-content: center;
      margin-top: 1.5rem;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._loadReports();
  }

  private async _loadReports() {
    this.isLoading = true;
    this.error = "";

    try {
      this.reports = await debugService.getReports(this.includeResolved);
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to load reports";
    } finally {
      this.isLoading = false;
    }
  }

  private _handleViewReport(reportId: string) {
    Router.go(`/admin/debug-logs/${reportId}`);
  }

  private _handleDeleteClick(reportId: string, e: Event) {
    e.stopPropagation();
    this.deleteConfirmId = reportId;
  }

  private async _handleConfirmDelete() {
    if (!this.deleteConfirmId) return;

    this.isDeleting = true;

    try {
      await debugService.deleteReport(this.deleteConfirmId);
      this.reports = this.reports.filter(
        (r) => r.reportId !== this.deleteConfirmId,
      );
      this.deleteConfirmId = null;
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to delete report";
    } finally {
      this.isDeleting = false;
    }
  }

  private _handleToggleResolved() {
    this.includeResolved = !this.includeResolved;
    this._loadReports();
  }

  render() {
    const auth = authService.state;
    if (!auth.isAuthenticated || !auth.isAdmin) {
      return html`
        <app-shell pageTitle="Debug Logs">
          <wa-callout variant="danger" open>
            <wa-icon slot="icon" name="circle-exclamation"></wa-icon>
            Admin access required
          </wa-callout>
        </app-shell>
      `;
    }

    return html`
      <app-shell pageTitle="Debug Logs">
        <div class="page-content">
          <h1>Debug Reports</h1>
          <p class="subtitle">
            User-submitted debug reports for troubleshooting issues
          </p>

          ${this.error
            ? html`
                <wa-callout variant="danger" open>
                  <wa-icon slot="icon" name="circle-exclamation"></wa-icon>
                  ${this.error}
                </wa-callout>
              `
            : ""}

          <div class="toolbar">
            <wa-button variant="neutral" @click=${() => this._loadReports()}>
              <wa-icon slot="prefix" name="arrow-rotate-right"></wa-icon>
              Refresh
            </wa-button>
            <div class="toolbar-spacer"></div>
            <label style="display: flex; align-items: center; gap: 0.5rem;">
              <wa-switch
                ?checked=${this.includeResolved}
                @change=${this._handleToggleResolved}
              ></wa-switch>
              Show resolved
            </label>
          </div>

          ${this.isLoading
            ? html`
                <div class="empty-state">
                  <wa-spinner></wa-spinner>
                  <p>Loading reports...</p>
                </div>
              `
            : this.reports.length === 0
              ? html`
                  <div class="empty-state">
                    <wa-icon name="inbox"></wa-icon>
                    <p>No debug reports found</p>
                  </div>
                `
              : this._renderTable()}
        </div>

        ${this._renderDeleteDialog()}
      </app-shell>
    `;
  }

  private _renderTable() {
    return html`
      <table class="reports-table">
        <thead>
          <tr>
            <th>Page</th>
            <th>Issue</th>
            <th>User</th>
            <th>Submitted</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${this.reports.map(
            (report) => html`
              <tr
                class=${report.isResolved ? "resolved" : ""}
                @click=${() => this._handleViewReport(report.reportId)}
              >
                <td class="page-name">${report.pageName}</td>
                <td class="user-note">${report.userNote}</td>
                <td class="user-info">
                  <div>${report.userDisplayName}</div>
                  <div class="user-email">${report.userEmail}</div>
                </td>
                <td>
                  ${format(new Date(report.createdAt), "MMM d, yyyy HH:mm")}
                </td>
                <td>
                  ${report.isResolved
                    ? html`<wa-badge variant="neutral">Resolved</wa-badge>`
                    : html`<wa-badge variant="warning">Open</wa-badge>`}
                </td>
                <td class="actions">
                  <wa-button
                    variant="danger"
                    size="small"
                    @click=${(e: Event) =>
                      this._handleDeleteClick(report.reportId, e)}
                  >
                    <wa-icon name="trash"></wa-icon>
                  </wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    `;
  }

  private _renderDeleteDialog() {
    return html`
      <wa-dialog
        class="delete-dialog"
        ?open=${this.deleteConfirmId !== null}
        @wa-request-close=${() => (this.deleteConfirmId = null)}
        label="Confirm Delete"
      >
        <div class="delete-dialog-content">
          <wa-icon name="triangle-exclamation"></wa-icon>
          <h3>Delete Debug Report?</h3>
          <p>This action cannot be undone.</p>

          <div class="delete-dialog-actions">
            <wa-button
              variant="neutral"
              @click=${() => (this.deleteConfirmId = null)}
              ?disabled=${this.isDeleting}
            >
              Cancel
            </wa-button>
            <wa-button
              variant="danger"
              @click=${this._handleConfirmDelete}
              ?loading=${this.isDeleting}
            >
              Delete
            </wa-button>
          </div>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "debug-logs-page": DebugLogsPage;
  }
}
